import { memo } from 'react';
import type { Avatar as AvatarT } from '@/api/types';
import { buildAvatar, INK } from '@/lib/avatar';
import { cn } from '@/lib/cn';

export interface AvatarProps {
  avatar: Partial<AvatarT> | null | undefined;
  size?: number;
  className?: string;
  /** Accessible name; omit for decorative use. */
  label?: string;
  online?: boolean;
  ring?: boolean;
  /** Cut-out look: a bone outline around the shape, for blobs stuck on a colour field. */
  sticker?: boolean;
}

export const Avatar = memo(function Avatar({ avatar, size = 48, className, label, online, ring, sticker }: AvatarProps) {
  const s = buildAvatar(avatar);
  return (
    <span className={cn('relative inline-block shrink-0', className)} style={{ width: size, height: size }}>
      <svg
        viewBox="-4 -4 108 108"
        width={size}
        height={size}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        className={cn(ring && 'rounded-full ring-2 ring-fg')}
      >
        {sticker && <path d={s.shapePath} fill="#F2EEE5" stroke="#F2EEE5" strokeWidth={8} strokeLinejoin="round" />}
        <path
          d={s.shapePath}
          fill={s.fill}
          stroke={s.softenStroke ? s.fill : undefined}
          strokeWidth={s.softenStroke || undefined}
          strokeLinejoin="round"
        />
        <g transform={s.transform}>
          {s.face.map((p, i) =>
            p.kind === 'circle' ? (
              <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill={INK} />
            ) : p.fill ? (
              <path key={i} d={p.d} fill={INK} />
            ) : (
              <path key={i} d={p.d} fill="none" stroke={INK} strokeWidth={p.width ?? 4} strokeLinecap="round" strokeLinejoin="round" />
            ),
          )}
        </g>
      </svg>
      {online !== undefined && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2 border-ground',
            online ? 'bg-green' : 'bg-line',
          )}
          style={{ width: Math.max(10, size * 0.26), height: Math.max(10, size * 0.26) }}
          aria-label={online ? 'Online' : 'Offline'}
          role="img"
        />
      )}
    </span>
  );
});
